import React, { useEffect, useState, useMemo } from "react";
import { Button, Card, CardBody, CardHeader, Col, Container, Row, Spinner, Input } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuthUser } from "../../../helpers/utils";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers, fetchCourseSessionsTableColumnNames, fetchAllCourseSessions, createCourseSessions, updateCourseSessions, 
  deleteCourseSessions, updateUserPreference, cancelCourseSessions, getQuestionTypes, fetchAllQuestions, 
  fetchQuestionsTableColumnNames,
  getDifficultyLevel,
  fetchAllTopics} from "../../../slices/thunks";
import ColumnSelectionModal from "../../../Components/Common/ColumnSelectionModal/ColumnSelectionModal";
import AddCourseSessionForm from "../../../Components/Common/AddCourseForm/AddCourseSessionForm";
import AddAssignmentForm from "../../../Components/Common/AddAssignmentForm/AddAssignmentForm";
import DeleteModal from "../../../Components/Common/DeleteModal";
import ViewModal from "../../../Components/Common/Modal/ViewModal";
import CommonDataTable from "../../../Components/Common/Table/CommonDataTable";
import CommonAddModal from "../../../Components/Common/Modal/CommonAddModal";
import usePermissions from "../../../Components/Hooks/usePermissions";
import { toast } from "react-toastify";
import { ErrorMessage } from "formik";


const addLabels = [
  {
    id: 1,
    labelName: "Tutor",
    fieldName: "tutor-dropdown",
    name: "TutorID",
    value: "TutorID",
    required: true,
    errorMessage: "Tutor is required"
  },
  {
    id: 2,
    labelName: "NoOfSessions",
    fieldName: "number-box",
    name: "NoOfSessions",
    value: "NoOfSessions",
    required: true,
    errorMessage: "Number of sessions is required"
  },
  {
    id: 3,
    labelName: "SessionDate",
    fieldName: "date-picker",
    name: "SessionDates",
    value: "SessionDates",
    required: true,
    isMultiple: true,
    errorMessage: "Session dates are required"
  },
  {
    id: 4,
    labelName: "Start Time",
    fieldName: "time",
    name: "StartDate",
    value: "StartDate",
    required: true,
    errorMessage: "Start time is required"
  }
]

const editLabels = [
  {
    id: 1,
    labelName: "Tutor",
    fieldName: "tutor-dropdown",
    name: "TutorID",
    value: "TutorID",
    required: true,
    errorMessage: "Tutor is required"
  },
  {
    id: 3,
    labelName: "SessionDate",
    fieldName: "date-picker",
    name: "SessionDate",
    value: "SessionDate",
    required: true,
    errorMessage: "Session date is required",
  },
  {
    id: 4,
    labelName: "Start Time",
    fieldName: "time",
    name: "StartDate",
    value: "StartDate",
    required: true,
    errorMessage: "Start time is required",
  }
]

const assignmentLabels = [
  // {
  //   id: 1,
  //   labelName: "Type",
  //   fieldName: "type-dropdown",
  //   name: "QuestionTypeID",
  //   value: "QuestionTypeID",
  //   required: true,
  // },
  {
    id: 2,
    labelName: "Question",
    fieldName: "question-list",
    name: "QuestionID",
    value: "QuestionID",
    required: true,
  }
]

const actionConfig = {
  cancel: {
    icon: "bx bxs-x-circle",
    tooltip: "cancel",
    isVisible: (item) => item.SessionStatusID != 4,
  },
  edit: {
    icon: "ri-edit-2-line",
    tooltip: "Edit",
    isVisible: (item) => item.SessionStatusID != 4,
  },
  delete: {
    icon: "ri-delete-bin-2-line",
    tooltip: "Delete",
    isVisible: (item) => item.SessionStatusID != 4,
  },
};


function CourseSessions () {
  const { moduleId, courseId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const {user} = getAuthUser();
  const [show, setShow] = useState(false);
  const [showAssignment, setShowAssignment] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOrEdit, setIsAddOrEdit] = useState("isAdd");
  const [selectedRow, setSelectedRow] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState([]);

  const { Module, CourseSessions, tableColumnNames, loading } = useSelector((state) => ({
    Module: state.CourseSessions.module,  
    CourseSessions: state.CourseSessions.sessions, 
    tableColumnNames: state.CourseSessions.tableColumnNames,
    loading: state.CourseSessions.loading,
    module: state.Module.module,
  }));  

  // Compute permission
  const permission = (() => {
    // moduleId: 1 (Administration), pageId: 7 (Course)
    return usePermissions(8, 7);
  })();

  const handleButtonClick = (session, type) => {
    let IsAssignment = true
    dispatch(fetchAllQuestions({ CourseID: parseInt(courseId), SessionID: session.ID, IsAssignment: IsAssignment }))

    if (session.QuestionIDs?.length > 0) {
      setIsAddOrEdit("isEdit")
    } else {
      setIsAddOrEdit("isAdd")
    }
    setSelectedRow({ TopicID:Module.TopicID, ...session});
    
    setShowAssignment(true);
  }

  const onAddClickHandler = (value) => {
    setShow(true);
    setIsAddOrEdit(value);
    
    setSelectedRow({ TutorID: Module.TutorID });
  };

  const onEditClickHandler = (session, value) => {
    const time = new Date(session.StartDate).toLocaleTimeString();
    setSelectedRow({ 
      ...session, 
      SessionDate: session.StartDate,
      StartDate: time
     });
    setIsAddOrEdit(value);
    setShow(true);
  };

  const onDeleteClickHandler = (session) => {
    setSelectedIds([session.ID])
    setDeleteModal(true);
  };

  const onCancelClickHandler = (item) => {
    dispatch(cancelCourseSessions({ID:item.ID}))
  };
  

  // Inline Edit handler
  const onSaveInLineHandler = (item, title, inputValue) => {
    const sessionObj = {
      ID: item.ID,
      [title]: inputValue
    }
    
    dispatch(updateCourseSessions(sessionObj))
  }

  const onChangeHandler = (e) => {
  const { name, value } = e.target;

  setSelectedRow((prev) => ({
    ...prev,
    [name]: value
  }));
};
  
  
const onSubmitHandler = async (values, { resetForm, setSubmitting }) => {
    setSubmitting(true);
    try {
      const [hours, minutes] = values.StartDate.split(":").map(Number);

        if (isAddOrEdit === 'isAdd') {
          const sessionDates = (values.SessionDates || []).map((scheduleDate) => {
            const date = new Date(scheduleDate);

            // Set time on the date
            date.setHours(hours);
            date.setMinutes(minutes);
            date.setSeconds(0);
            date.setMilliseconds(0);

            // Return new ISO string with time
            return date.toISOString();
          });

          // Check if NoOfSessions matches SessionDates length
          if (values.NoOfSessions !== sessionDates.length) {
            toast.error(t("Number of sessions must match the number of selected dates"));
            setSubmitting(false);
            return;
          }

          const sessionObj = {
            ModuleID: moduleId,
            TutorID: values.TutorID, 
            NoOfSessions: values.NoOfSessions, 
            SessionDates: sessionDates
          };
          setShow(false);
          await dispatch(createCourseSessions(sessionObj))

        } else { 

          const date = new Date(values.SessionDate); 

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(0);
          date.setMilliseconds(0);

        const sessionObj = {
          ID: selectedRow.ID,
          TutorID: selectedRow.TutorID,
          StartDate: date.toISOString()
        };

        dispatch(updateCourseSessions(sessionObj));
        setShow(false);
        resetForm(); 
        }
      }
    catch (error) {
      console.error("Error submitting session:", error);
      toast.error(t("SubmissionFailed") + (error.message ? `: ${error.message}` : '')); 
    } finally {
      setSubmitting(false); 
    }
};
  
const handleSave = async () => {
    const preferencesPayload = {
      UserId: user.UserId,
      ColumnIDs: tableColumnNames.reduce((acc, column) => {
        acc[column.ColumnId] = selectedColumns.includes(column.ColumnId);
        return acc;
      }, {}),
    };

    setModalOpen(false);
    try {
      await dispatch(updateUserPreference(preferencesPayload)).unwrap();
      await dispatch(
        fetchCourseSessionsTableColumnNames({
          module: "COURSESESSIONS",
        })
      );

    } catch (error) {
      console.error("Error updating preferences:", error);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteCourseSessions({ Ids: selectedIds}))
      .then(() => {
        setSelectedIds([]);
      })
      .catch((error) => {
        console.error("Error deleting:", error);
      });
    setDeleteModal(false);
  };
  
  useEffect(() => {
    dispatch(fetchCourseSessionsTableColumnNames({ module: "COURSESESSIONS" }));
    dispatch(fetchQuestionsTableColumnNames({ module: "ASSIGNMENTQUESTIONS" }));
    dispatch(fetchAllCourseSessions({ ModuleID: moduleId }));
    dispatch(fetchUsers({ IsSystemUser: false }));
    dispatch(fetchAllTopics({IsActive: true}));
    dispatch(getQuestionTypes())
    dispatch(fetchAllQuestions())
    dispatch(getDifficultyLevel())
  }, [dispatch, user.UserId]);

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <BreadCrumb title={i18n.language === 'en' ? Module?.ModuleNameEn : Module?.ModuleNameAr} back={true} />
          </Col>
        </Row>
        <div className="table-responsive">
          <Card>
            <CardHeader>
              <Row className="g-4">
                  <Col className="col-sm-auto">
                    <div>
                      <Button
                        className="btn btn-soft-danger ms-2 me-2"
                        onClick={() => setDeleteModal(true)}
                        disabled={selectedIds.length === 0}
                      >
                        <i className="ri-delete-bin-2-line"></i>
                      </Button>
                      {permission.CanAdd && (
                        <Button
                          color="secondary"
                          className="add-btn me-1"
                          onClick={() => onAddClickHandler("isAdd")}
                          id="create-btn"
                          disabled={loading}
                        >
                          <i className="ri-add-line align-bottom me-1 ms-1"></i>
                          {t("AddNew")}
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col className="col-sm">
                    <div className="d-flex justify-content-sm-end">
                      <Button
                        className="btn btn-soft"
                        onClick={() => setModalOpen(true)}
                        disabled={loading}
                      >
                        <i className="ri-filter-3-line"></i>
                      </Button>
                      <div className="search-box ms-2 me-2">
                        <Input
                          type="text"
                          className="form-control search"
                          placeholder={t("Search")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <div className="listjs-table" id="customerList">
                
                {loading ? (
                  <div className="d-flex justify-content-center align-items-center">
                  <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                    Loading...
                  </Spinner>
                  </div>
                ) : (
                  <>
                  <div className="listjs-table" id="customerList">
                    <CommonDataTable
                      data={CourseSessions}
                      tableColumnNames={tableColumnNames}
                      searchTerm={searchTerm}
                      onEditClickHandler={onEditClickHandler}
                      onInlineUpdate={onSaveInLineHandler}
                      handleCancel={onCancelClickHandler}
                      handleButtonClick={handleButtonClick}
                      setDeleteModal={setDeleteModal}
                      setSelectedIds={setSelectedIds}
                      actionConfig={actionConfig}
                      permission={permission}
                    />
                  </div>
                  </>
                )}
              </div>
            </CardBody>
          </Card>
        </div>
      </Container>

      {/* Modal  */}
      <CommonAddModal
        show={show}
        setShow={setShow}
        onSubmitHandler={onSubmitHandler}
        labels={isAddOrEdit === "isAdd" ? addLabels : editLabels}
        isAddOrEdit={isAddOrEdit}
        selectedItemData={selectedRow} 
        FormBody={AddCourseSessionForm}
        FormBodyProps={{ 
          Module, 
          CourseSessions, 
        }}
        title={isAddOrEdit === "isAdd" ? t("AddNewSession") : t("EditSession")}
        size="lg"
      />

      {/* Modal  */}
      <ViewModal
        show={showAssignment}
        setShow={setShowAssignment}
        title={t("Assignment")}
        modalBody={
          <AddAssignmentForm
            labels={assignmentLabels}
            isAddOrEdit={isAddOrEdit}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            onChangeHandler={onChangeHandler}
          />
        }
      />

      {/* ColumnSelectionModal */}
      <ColumnSelectionModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        columns={tableColumnNames}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        handleSave={handleSave}
      />

      {/* Delete modal  */}
      <DeleteModal
        showDelete={deleteModal}
        setShowDelete={setDeleteModal}
        onDeleteClick={confirmDelete}
        setSelectedIds={setSelectedIds}
        description={
          selectedIds.length > 1
            ? `Are you sure you want to delete ${selectedIds.length} selected items?`
            : `Are you sure you want to delete this record?`
        }
      />
    </div>
  );
}

export default CourseSessions;